import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Circle, Edit2, Trash2, Calendar, Clock, UserPlus, MessageCircle } from 'lucide-react';
import { Card } from './Card';
import { InviteFriendsModal } from './InviteFriendsModal';
import { QuestChatModal } from './QuestChatModal';
import { notifications } from '../lib/notifications';
interface GoalCardProps {
  goal: {
    id: string;
    title: string;
    category: string;
    priority: string;
    progress: number;
    completed: boolean;
    dueDate?: string;
    dueTime?: string;
    participants?: Array<{
      id: string;
      name: string;
      avatar: string;
    }>;
  };
  onToggle: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}
export function GoalCard({
  goal,
  onToggle,
  onEdit,
  onDelete
}: GoalCardProps) {
  const {
    title,
    category,
    priority,
    progress,
    completed,
    dueDate,
    dueTime,
    participants = []
  } = goal;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatTime = (timeString?: string) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  const handleInvite = async (emails: string[]) => {
    // In production, this would call your backend API
    console.log('Inviting users to quest:', goal.id, emails);
    notifications.success('Invites Sent!', `${emails.length} invitation${emails.length !== 1 ? 's' : ''} sent successfully`);
  };
  const isCollaborative = participants.length > 1;
  return <>
      <Card className="!p-4 relative hover:shadow-lg transition-shadow" data-id="element-264">
        <div className="flex items-start gap-3" data-id="element-265">
          <button onClick={onToggle} className="mt-1 flex-shrink-0 hover:scale-110 transition-transform" data-id="element-266">
            {completed ? <CheckCircle2 className="w-6 h-6 text-game-teal" data-id="element-267" /> : <Circle className="w-6 h-6 text-gray-300 dark:text-gray-600" data-id="element-268" />}
          </button>

          <div className="flex-1 min-w-0" data-id="element-269">
            <div className="flex items-start justify-between gap-2 mb-2" data-id="element-270">
              <h3 className={`font-fredoka font-medium text-base leading-tight break-words ${completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-game-dark dark:text-white'}`} data-id="element-271">
                {title}
              </h3>
              <span className={`
                  text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap flex-shrink-0
                  ${priority === 'High' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : priority === 'Medium' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'}
                `} data-id="element-272">
                {priority}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3" data-id="element-273">
              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md font-quicksand" data-id="element-274">
                {category}
              </span>
              <span data-id="element-275">•</span>
              <span className="font-quicksand" data-id="element-276">{progress}%</span>
              {isCollaborative && <>
                  <span data-id="element-277">•</span>
                  <span className="flex items-center gap-1 font-quicksand text-game-purple" data-id="element-278">
                    <UserPlus className="w-3 h-3" data-id="element-279" />
                    {participants.length} collaborators
                  </span>
                </>}
            </div>

            {/* Due Date and Time */}
            {(dueDate || dueTime) && <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-gray-600 dark:text-gray-400" data-id="element-280">
                {dueDate && <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg" data-id="element-281">
                    <Calendar className="w-3 h-3 flex-shrink-0" data-id="element-282" />
                    <span className="font-quicksand" data-id="element-283">
                      {formatDate(dueDate)}
                    </span>
                  </div>}
                {dueTime && <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg" data-id="element-284">
                    <Clock className="w-3 h-3 flex-shrink-0" data-id="element-285" />
                    <span className="font-quicksand" data-id="element-286">
                      {formatTime(dueTime)}
                    </span>
                  </div>}
              </div>}

            <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-3 border border-gray-200 dark:border-gray-600" data-id="element-287">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} className="h-full bg-gradient-to-r from-game-purple to-game-teal" data-id="element-288" />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2" data-id="element-289">
              <button onClick={() => setShowInviteModal(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors text-xs font-fredoka font-bold" data-id="element-290">
                <UserPlus className="w-3 h-3" data-id="element-291" />
                Invite
              </button>
              {isCollaborative && <button onClick={() => setShowChatModal(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors text-xs font-fredoka font-bold" data-id="element-292">
                  <MessageCircle className="w-3 h-3" data-id="element-293" />
                  Chat
                </button>}
              {onEdit && <button onClick={e => {
              e.stopPropagation();
              onEdit();
            }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-xs font-fredoka font-bold" data-id="element-294">
                  <Edit2 className="w-3 h-3" data-id="element-295" />
                  Edit
                </button>}
              {onDelete && <button onClick={e => {
              e.stopPropagation();
              onDelete();
            }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-xs font-fredoka font-bold" data-id="element-296">
                  <Trash2 className="w-3 h-3" data-id="element-297" />
                  Delete
                </button>}
            </div>
          </div>
        </div>
      </Card>

      {/* Modals */}
      <InviteFriendsModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} questId={goal.id} questTitle={title} onInvite={handleInvite} data-id="element-298" />

      <QuestChatModal isOpen={showChatModal} onClose={() => setShowChatModal(false)} questId={goal.id} questTitle={title} participants={participants} data-id="element-299" />
    </>;
}